import Vue from "vue";
import Vuex from "vuex";
import characterRepository from "@/services/characterRepository";
import dogmaRepository from "@/services/dogmaRepository";
import universeRepository from "@/services/universeRepository";
import corporationRepository from "@/services/corporationRepository";
import idbs from "../services/idb";

Vue.use(Vuex);

const getInitialState = () => {
  return {
    login_url: `https://login.eveonline.com/oauth/authorize?response_type=token&redirect_uri=${process.env.VUE_APP_REDIRECT_URL}&client_id=${process.env.VUE_APP_CLIENT_ID}&scope=publicData+esi-location.read_ship_type.v1+esi-skills.read_skills.v1+esi-fittings.read_fittings.v1+esi-fittings.write_fittings.v1+esi-clones.read_implants.v1+esi-characters.read_loyalty.v1`,
    base_url: "https://esi.evetech.net/latest",
    initial_content: [
      "loadSkills",
      "loadShips",
      "loadStoredCharacter",
    ],
    initial_load: true,
    skills: [],
    skill_groups: [],
    ships: [],
    ship_groups: [],
    dogma_attributes: [],
    loggedIn: false,
    access_token: null,
    character_id: null,
    character_name: null,
    character_total_sp: 0,
    character_skills: [],
    character_implants: [],
    character_loyalty: [],
    fittings: [],
    types: [],
    corporations: [],
    objectStores: [
      "ships",
      "ship_groups",
      "types",
      "dogma_attributes",
      "skills",
      "skill_groups",
      "fittings",
      "corporations"
    ]
  }
}

export default new Vuex.Store({
  state: getInitialState(),
  mutations: {
    SET_STATE(state, { field, data }) {
      Vue.set(state, field, data)
    },
    LOAD_SKILLS(state, payload) {
      state.skills = payload;
    },
    LOAD_SKILL_GROUPS(state, payload) {
      state.skill_groups = payload;
    },
    LOAD_SHIPS(state, payload) {
      state.ships = payload;
    },
    LOAD_SHIP_GROUPS(state, payload) {
      state.ship_groups = payload;
    },
    SET_LOADED(state, loading = false) {
      state.initial_load = loading;
    },
    LOAD_DOGMA_ATTRIBUTES(state, payload) {
      state.dogma_attributes.push(...payload);
    },
    SAVE_ACCESS_TOKEN(state, token) {
      state.access_token = token;
      state.loggedIn = true;
    },
    SAVE_CHARACTER(state, payload) {
      state.character_id = payload.id;
      state.character_name = payload.name;
      state.character_expires = payload.expires;
    },
    SAVE_CHARACTER_SKILLS(state, { total_sp, skills }) {
      state.character_total_sp = total_sp;
      state.character_skills = skills;
    },
    SAVE_CHARACTER_IMPLANTS(state, payload) {
      state.character_implants = payload;
    },
    SAVE_CHARACTER_LOYALTY(state, payload) {
      state.character_loyalty = payload;
    },
    SAVE_FITTINGS(state, payload) {
      state.fittings = payload;
    },
    LOAD_TYPES(state, payload) {
      state.types.push(...payload);
    },
    LOAD_CORPORATIONS(state, payload) {
      state.corporations.push(...payload);
    },
    OVERWRITE_CORP(state, payload) {
      const index = state.corporations
        .findIndex(x => x.corporation_id == payload.corporation_id);
      state.corporations.splice(index, 1, payload);
    },
    DELETE_FITTING(state, fitting_id) {
      const index = state.fittings.findIndex(x => x.fitting_id == fitting_id);
      state.fittings.splice(index, 1);
    },
    RESET_STORE(state) {
      Object.assign(state, getInitialState());
    }
  },
  actions: {
    loadInitialContent({ commit, state, dispatch }) {
      dispatch("checkStorage").then(() => {
        Promise.all(state.initial_content.map(x => dispatch(x))).then(() =>
          commit("SET_LOADED")
        );
      });
    },
    loadSkills({ commit, dispatch, state }) {
      if (state.skills.length > 0) {
        return Promise.resolve();
      }
      return universeRepository
        .getCategory(16)
        .then(({ data }) => {
          return Promise.all(
            data.groups.map(x => universeRepository.getGroup(x))
          );
        })
        .then(groups => {
          const published_groups = groups
            .map(x => x.data)
            .filter(x => x.published);
          commit("LOAD_SKILL_GROUPS", published_groups);
          return Promise.all(
            published_groups
              .map(x => x.types)
              .flat()
              .map(x => universeRepository.getType(x))
          );
        })
        .then(skills => {
          commit("LOAD_SKILLS", skills.map(x => x.data));
          dispatch("saveToStorage");
        });
    },
    loadShips({ commit, dispatch, state }) {
      if (state.ships.length > 0) {
        return Promise.resolve();
      }
      return universeRepository
        .getCategory(6)
        .then(({ data }) => {
          return Promise.all(
            data.groups.map(x => universeRepository.getGroup(x))
          );
        })
        .then(groups => {
          const published_groups = groups
            .map(x => x.data)
            .filter(x => x.published);
          commit("LOAD_SHIP_GROUPS", published_groups);
          const ship_ids = groups
            .filter(x => x.data.published)
            .map(x => x.data.types)
            .flat();
          return Promise.all(ship_ids.map(x => universeRepository.getType(x)));
        })
        .then(ships => {
          const published_ships = ships
            .map(x => x.data)
            .filter(x => x.published)
          commit("LOAD_SHIPS", published_ships);
          dispatch("saveToStorage");
        });
    },
    loadDogmaAttributes({ commit, state, dispatch }, attribute_ids) {
      const loaded_ids = state.dogma_attributes.map(x => x.attribute_id);
      const still_to_load = attribute_ids.filter(x => !loaded_ids.includes(x));
      return Promise.all(
        still_to_load.map(x => dogmaRepository.getAttribute(x))
      ).then(dogma_attributes => {
        commit("LOAD_DOGMA_ATTRIBUTES", dogma_attributes.map(x => x.data));
        dispatch("saveToStorage");
        return state.dogma_attributes.filter(x =>
          attribute_ids.includes(x.attribute_id)
        );
      });
    },
    async clearCache({ commit, dispatch }) {
      commit("SET_LOADED", true);
      commit("RESET_STORE");
      window.localStorage.removeItem("character");
      window.localStorage.removeItem("character_skills");
      window.localStorage.removeItem("character_implants");
      window.localStorage.removeItem("character_loyalty");
      window.localStorage.removeItem("redirect_route");
      await idbs.clearStorage();
      dispatch("loadInitialContent");
    },
    verifyCharacterId({ commit }, access_token) {
      commit("SAVE_ACCESS_TOKEN", access_token);
      characterRepository.verify(access_token).then(({ data }) => {
        const character = {
          id: data.CharacterID,
          name: data.CharacterName
        };
        window.localStorage.setItem("character", JSON.stringify(character));
        commit("SAVE_CHARACTER", character);
      });
    },
    loadCharacterSkills({ commit, state }) {
      return characterRepository
        .getSkills(state.character_id)
        .then(({ data }) => {
          const groups = {};
          const ordered_groups = Object.values(
            state.skill_groups
          ).sort((a, b) => (a.name > b.name ? 1 : -1));
          for (const group of ordered_groups) {
            groups[group.name] = group.types
              .map(x => {
                const trained_skills = data.skills.filter(y => y.skill_id == x);
                return {
                  skill: state.skills.find(y => y.type_id == x),
                  level: trained_skills[0]
                    ? trained_skills[0].active_skill_level
                    : null
                };
              })
              .filter(x => x.skill.published)
              .sort((a, b) => (a.skill.name > b.skill.name ? 1 : -1));
          }
          const character_skills = {
            total_sp: data.total_sp,
            skills: groups
          };
          window.localStorage.setItem(
            "character_skills",
            JSON.stringify(character_skills)
          );
          commit("SAVE_CHARACTER_SKILLS", character_skills);
        });
    },
    loadCharacterImplants({ commit, dispatch, state }) {
      if (state.character_implants.length != 0) {
        return Promise.resolve(state.character_implants);
      }
      return characterRepository.getImplants()
        .then(({ data }) => data)
        .then(type_ids => {
          commit("SAVE_CHARACTER_IMPLANTS", type_ids);
          window.localStorage.setItem(
            "character_implants",
            JSON.stringify(type_ids)
          );
          dispatch("loadTypes", type_ids);
          return type_ids;
        });
    },
    loadCharacterLoyalty({ commit, dispatch }) {
      return characterRepository.getLoyalty()
        .then(({ data }) => data)
        .then(loyalty_levels => {
          commit("SAVE_CHARACTER_LOYALTY", loyalty_levels);
          window.localStorage.setItem(
            "character_loyalty",
            JSON.stringify(loyalty_levels)
          );
          dispatch("loadCorps", loyalty_levels.map(x => x.corporation_id));
          return loyalty_levels;
        });
    },
    loadStoredCharacter({ commit }) {
      if (window.localStorage.getItem("character")) {
        commit(
          "SAVE_CHARACTER",
          JSON.parse(window.localStorage.getItem("character"))
        );
      }
      if (window.localStorage.getItem("character_skills")) {
        commit(
          "SAVE_CHARACTER_SKILLS",
          JSON.parse(window.localStorage.getItem("character_skills"))
        );
      }
      if (window.localStorage.getItem("character_implants")) {
        commit(
          "SAVE_CHARACTER_IMPLANTS",
          JSON.parse(window.localStorage.getItem("character_implants"))
        );
      }
      if (window.localStorage.getItem("character_loyalty")) {
        commit(
          "SAVE_CHARACTER_LOYALTY",
          JSON.parse(window.localStorage.getItem("character_loyalty"))
        );
      }
      return Promise.resolve();
    },
    loadFittings({ commit, dispatch }) {
      return characterRepository.getFittings().then(({ data }) => {
        window.localStorage.setItem("fittings", JSON.stringify(data));
        commit("SAVE_FITTINGS", data);
        dispatch("loadTypes", [
          ...new Set(data.map(x => x.items.map(y => y.type_id)).flat())
        ]);
      });
    },
    loadTypes({ commit, state, dispatch }, type_ids) {
      const loaded_ids = state.types.map(x => x.type_id);
      const types_to_load = type_ids.filter(x => !loaded_ids.includes(x));
      return Promise.all(
        types_to_load.map(x => universeRepository.getType(x))
      ).then(types => {
        commit("LOAD_TYPES", types.map(x => x.data));
        dispatch("saveToStorage");
        return state.types.filter(x => type_ids.includes(x.type_id));
      });
    },
    loadCorps({ commit, state, dispatch }, corp_ids) {
      const loaded_ids = state.corporations.map(x => x.type_id);
      const types_to_load = corp_ids.filter(x => !loaded_ids.includes(x));
      return Promise.all(
        types_to_load.map(x => corporationRepository.getCorp(x))
      ).then(corps => {
        const corps_with_ids = corps.map((x, index) => {
          return { ...x.data, corporation_id: types_to_load[index] };
        });
        commit("LOAD_CORPORATIONS", corps_with_ids);
        dispatch("saveToStorage");
        return state.corporations.filter(x => corp_ids.includes(x.corporation_id));
      });
    },
    loadLoyaltyStore({ commit, state, dispatch }, corp_id) {
      const corp = state.corporations.find(x => x.corporation_id == corp_id);
      return corporationRepository.getLoyaltyStore(corp_id)
        .then(({ data }) => {
          const items = data.filter(x => !x.ak_cost);
          corp.loyalty_items = items;
          commit("OVERWRITE_CORP", corp);
          dispatch("saveToStorage");
          dispatch("loadTypes", items.map(x => x.type_id));
        });
    },
    deleteFitting({ commit, dispatch }, fitting_id) {
      return characterRepository.deleteFitting(fitting_id).then(() => {
        commit("DELETE_FITTING", fitting_id);
        dispatch("saveToStorage");
      });
    },
    checkStorage({ state, commit }) {
      return Promise.all(state.objectStores.map(async field => {
        try {
          let data = await idbs.checkStorage(field) || [];

          commit("SET_STATE", { field, data })
        } catch (e) {
          // The value in storage was invalid or corrupt so just set it to blank
          commit("SET_STATE", { field, data: [] })
        }
        return Promise.resolve();
      }));
    },
    async saveToStorage({ state }) {
      await Promise.all(state.objectStores.map(field => idbs.saveToStorage(field, state[field])))
    }
  },
  modules: {}
});
